import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useFormik } from "formik";
import { useContext, useEffect, useState } from "react";
import { ContextOperation } from "../../../context/OperationContext";
import useOperationTranslation from "../../../hooks/useOperationTranslation";
import useSnackbar from "../../../hooks/useSnackbar";
import AutoComplete, { AutoCompleteOption } from "../../common/AutoComplete";
import { nameof } from "../../common/ObjectUtils";
import EquipmentAssociationService, { EquipmentAssociationRequests } from "../EquipmentAssociationService";
import { EquipmentAssociationTypes } from "../Types";
import { getAllAcriss, getAllKits } from "../Utils";

interface Props {
  tenant:string;
  open: boolean;
  onClose: () => void;
  acriss?: EquipmentAssociationTypes.ACRISS;
  updateDatasourceKit: () => void;
  updateDatasourceAcriss: () => void;
}

const EditEquipmentKitAssociation = (props: Props) => {
 
  const [listAcriss, setListAcriss] = useState<AutoCompleteOption[]>([]);
  const [listKits, setListKits] = useState<AutoCompleteOption[]>([]);

  const snackbar = useSnackbar();
  const t = useOperationTranslation();

  useEffect(() => {
    getAllAcriss(props.tenant).then(response => setListAcriss(response));
    getAllKits(props.tenant).then(response => setListKits(response));
  }, [props.tenant]);

  const handleSubmit = (data: EquipmentAssociationRequests.Create) => {
    EquipmentAssociationService.put.edit(props.tenant,data).then(
      () => {
        onClose();
        props.updateDatasourceKit();
        snackbar.show("success", t("vehicleEquipmentAssociation.dialogs.editAcriss.messages.success"));
      },
      () => {
        onClose();
        snackbar.show("error", t("vehicleEquipmentAssociation.dialogs.editAcriss.messages.error"));
      }
    )
  }

  const formik = useFormik<EquipmentAssociationRequests.Create>({
    initialValues: {
      equipmentKitsId: [],
      ids: [],
    },
    onSubmit: handleSubmit
  });

  const onClose = () => {
    props.onClose();
    formik.resetForm();
  }

  useEffect(() => {
    const acrissId = props.acriss?.id || null;
    if (acrissId) {
      EquipmentAssociationService.get.acrissKitsById(props.tenant,acrissId).then(
        response => {
          formik.setValues({
            ids: [acrissId],
            equipmentKitsId: response.map((kit) => kit.id),
          })
        }
      )
    }
  }, [props.open]);

  return (
    <Mui.Dialog open={props.open} onClose={onClose} fullWidth={true}>
      <Mui.DialogTitle>
        <Mui.Grid container alignItems="center">
          <Mui.Grid item xs="auto">
            <Mui.Typography variant="h6">{t("vehicleEquipmentAssociation.dialogs.editAcriss.title")}</Mui.Typography>
          </Mui.Grid>

          <Mui.Grid item xs="auto" marginLeft="auto">
            <Mui.IconButton onClick={onClose}>
              <MuiIcons.Close />
            </Mui.IconButton>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogTitle>

      <Mui.DialogContent>
        <form id="form-create-equipment-kit-association" onSubmit={formik.handleSubmit}>
          <Mui.Grid container spacing="16px">
            <Mui.Grid item md={6} xs={12}>
              <AutoComplete
                options={listAcriss}
                fullWidth={true}
                multiple={true}
                label={t("vehicleEquipmentAssociation.dialogs.editAcriss.form.acriss")}
                onChange={(value) => formik.setFieldValue(nameof<EquipmentAssociationRequests.Create>("ids"), value)}
                value={formik.values.ids}
              />
            </Mui.Grid>

            <Mui.Grid item md={6} xs={12}>
              <AutoComplete
                options={listKits}
                fullWidth={true}
                multiple={true}
                label={t("vehicleEquipmentAssociation.dialogs.editAcriss.form.equipmentKit")}
                onChange={(value) => formik.setFieldValue(nameof<EquipmentAssociationRequests.Create>("equipmentKitsId"), value)}
                value={formik.values.equipmentKitsId}
              />
            </Mui.Grid>
          </Mui.Grid>
        </form>
      </Mui.DialogContent>

      <Mui.DialogActions>
        <Mui.Grid container justifyContent="end" spacing="16px">
          <Mui.Grid item xs="auto">
            <Mui.Button variant="outlined" onClick={onClose}>
              {t("vehicleEquipmentAssociation.dialogs.editAcriss.actions.cancel")}
            </Mui.Button>
          </Mui.Grid>

          <Mui.Grid item xs="auto">
            <Mui.Button type="submit" onClick={formik.submitForm}>
              {t("vehicleEquipmentAssociation.dialogs.editAcriss.actions.save")}
            </Mui.Button>
          </Mui.Grid>
        </Mui.Grid>
      </Mui.DialogActions>
    </Mui.Dialog>
  )
}

export default EditEquipmentKitAssociation;